import type { UserRole } from '@lms/shared'
import { GraduationCap } from 'lucide-react'
import { Link } from 'react-router-dom'
import { UserMenu } from '@/components/auth/UserMenu'
import { useAuth } from '@/lib/auth'
import { useTenant, useTenantPath } from '@/lib/tenant'

interface NavLink {
  path: string
  label: string
  className?: string
}

type NavPreset = 'dashboard' | 'teacher' | 'admin' | 'learning'

export function getNavLinks(
  preset: NavPreset,
  role: UserRole,
  tp: (p: string) => string,
): NavLink[] {
  const base: NavLink[] = [
    { path: tp('/courses'), label: 'コース一覧' },
    { path: tp('/dashboard'), label: 'ダッシュボード' },
  ]

  // 受講管理（研修）。learning プリセットは受講中の集中画面なので追加しない
  if (preset !== 'learning') {
    base.push(
      { path: tp('/trainings'), label: '研修一覧' },
      { path: tp('/my-enrollments'), label: 'マイ受講' },
      { path: tp('/approvals'), label: '承認待ち' },
    )
  }

  const roleLinks: NavLink[] = []
  if (role === 'instructor' || role === 'admin' || role === 'superadmin') {
    roleLinks.push({
      path: tp('/teacher'),
      label: '講師メニュー',
      className: 'text-emerald-600 font-medium',
    })
  }
  if (role === 'admin' || role === 'superadmin') {
    roleLinks.push({
      path: tp('/admin/enrollments'),
      label: '受講状況管理',
      className: 'text-red-600 hover:text-red-800 font-medium',
    })
  }

  const sectionLinks: NavLink[] = []
  if (preset === 'teacher') {
    sectionLinks.push(
      { path: tp('/teacher/courses'), label: 'コース管理' },
      { path: tp('/teacher/lessons'), label: 'レッスン管理' },
    )
  }
  if (preset === 'admin') {
    sectionLinks.push({ path: tp('/admin/users'), label: 'ユーザー管理' })
  }

  return [...base, ...roleLinks, ...sectionLinks]
}

interface UnifiedNavigationProps {
  preset?: NavPreset
  title?: string
}

export function UnifiedNavigation({
  preset = 'dashboard',
  title = 'JBCCHD 受講管理システム',
}: UnifiedNavigationProps) {
  const { user } = useAuth()
  const tenant = useTenant()
  const tp = useTenantPath()
  const role = user?.role ?? 'student'

  const navLinks = getNavLinks(preset, role, tp)

  return (
    <header className="bg-white shadow-sm">
      <div className="container mx-auto px-4 py-4 flex justify-between items-center">
        <div className="flex items-center space-x-2">
          <Link to={`/${tenant.slug}/dashboard`} className="flex items-center space-x-2">
            <GraduationCap className="h-8 w-8 text-emerald-600" />
            <span className="text-xl font-bold">{title}</span>
          </Link>
        </div>
        <nav className="hidden md:flex space-x-8">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className={link.className || 'text-gray-700 hover:text-emerald-600 font-medium'}
            >
              {link.label}
            </Link>
          ))}
        </nav>
        <div className="flex items-center space-x-4">
          <UserMenu />
        </div>
      </div>
    </header>
  )
}
